import * as React from 'react';

import { LocalizedText } from 'bernie-l10n';

import { LocalizedComponentProps, withLocalization } from '@shared-ui/localization-context';
import { ViewMedium, Viewport, ViewSmall } from '@shared-ui/viewport-context';

import { SearchCriteriaModel } from 'src/common/stores/models';

import { UitkExpandoLink } from 'uitk-react-expando';
import { UitkHeading } from 'uitk-react-text';
import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkSpacing } from 'uitk-react-spacing';

import { DateUtils } from 'utils/date-utils';

import { CarPickUpDropOffTimeSelect } from 'components/multi-item/search-wizard/car-pickup-dropoff-time-select';
import { FormFieldBinding } from 'src/common/components/common/search-form/fields/form-field-binding';
import { getBexApiDateTimeFromLocKey } from 'components/multi-item/bex-api-date-time-to-time-loc-key';
import { SearchFormFieldProps } from 'src/common/components/common/search-form';

interface CarPickUpDropOffProps extends LocalizedComponentProps, SearchFormFieldProps {
  model: SearchCriteriaModel;
  validators: any[];
  updateModelCallback: (event?: Event) => void;
}

interface CarPickUpDropOffState {
  invalidText: string;
}

// @ts-ignore
@withLocalization
@FormFieldBinding
export class CarPickupDropOffTimeRange extends React.Component<CarPickUpDropOffProps, CarPickUpDropOffState> {
  private dropOffRef: any = undefined;
  private invalidDropOffErrorMessage: string;
  private pickUpTimeLabel: string;
  private dropOffTimeLabel: string;
  private pickUpDropOffToggleText: string;
  private dateUtils: DateUtils;

  private expandoLinkIsVisible = false;
  private expandoIsVisible = false;

  constructor(props: CarPickUpDropOffProps) {
    super(props);

    const { l10n, registerValidator } = props;

    this.timeRangeValidator = this.timeRangeValidator.bind(this);
    this.focusDropOff = this.focusDropOff.bind(this);

    this.invalidDropOffErrorMessage = l10n.formatText('multiItem.searchWizard.dropOffErrorMsg');
    this.pickUpTimeLabel = l10n.formatText('multiItem.searchWizard.pickUpTime');
    this.dropOffTimeLabel = l10n.formatText('multiItem.searchWizard.dropOffTime');
    this.pickUpDropOffToggleText = l10n.formatText('multiItem.searchWizard.pickUpDropOffToggle');

    this.dateUtils = new DateUtils(l10n);

    registerValidator && registerValidator(this.timeRangeValidator);

    this.dropOffRef = React.createRef();
    this.state = {
      invalidText: '',
    };
  }

  public componentDidMount() {
    this.setInvalidText();
  }

  public componentDidUpdate() {
    this.setInvalidText();
  }

  private setInvalidText() {
    const isCurrentTimeRangeValid = !this.timeRangeInvalid;
    const isLastTimeRangeValid = !this.state.invalidText;

    if (isCurrentTimeRangeValid && !isLastTimeRangeValid) {
      this.setState({ invalidText: '' });
    }

    if (!isCurrentTimeRangeValid && isLastTimeRangeValid) {
      this.setState({ invalidText: this.invalidDropOffErrorMessage });
    }
  }

  private focusDropOff() {
    this.dropOffRef.current.focus();
  }

  private timeRangeValidator() {
    const text = this.timeRangeInvalid ? this.invalidDropOffErrorMessage : '';

    this.setState({ invalidText: text });

    if (text) {
      return {
        text,
        focus: this.focusDropOff,
      };
    }

    return '';
  }

  private getTimeInMinutes(timeField: any) {
    const dateTimeFromLocKey: { hour: number; minute: number } = getBexApiDateTimeFromLocKey(timeField);
    const minutesInHour = 60;

    return dateTimeFromLocKey.hour * minutesInHour + dateTimeFromLocKey.minute;
  }

  private get dropOffTimeInMinutes() {
    if (!this.props.model.startDate || !this.props.model.endDate) return 0;
    const daysBetween = this.dateUtils.getDaysBetween(this.props.model.startDate, this.props.model.endDate);
    const minutesInDay = 24 * 60;
    const dropOffTime = this.getTimeInMinutes(this.props.model.dropOffTime);

    return daysBetween * minutesInDay + dropOffTime;
  }

  private get pickUpTimeInMinutes() {
    return this.getTimeInMinutes(this.props.model.pickUpTime);
  }

  private get timeRangeInvalid(): boolean {
    const { model } = this.props;
    if (!model.startDate || !model.endDate) {
      return false;
    }

    if (!model.pickUpTime || !model.dropOffTime) {
      return true;
    }

    return this.dropOffTimeInMinutes - this.pickUpTimeInMinutes < 120;
  }

  private getFormattedDate(rawDate: string) {
    try {
      return this.props.l10n.formatDateString(rawDate, { skeleton: 'MMMd' }) || undefined;
    } catch (e) {
      return undefined;
    }
  }

  public render() {
    const { l10n, updateModel: onChange, updateModelCallback, model, validators, modelKey } = this.props;

    const { pickUpTime, dropOffTime, startDate: pickUpDate, endDate: dropOffDate } = model;

    const commonProps = {
      updateModelCallback,
      model,
      modelKey,
      validators,
      onChange,
      l10n,
    };

    const timeSelect = (
      <UitkLayoutGrid columns={2} space="two">
        <UitkLayoutGridItem>
          <CarPickUpDropOffTimeSelect
            label={this.pickUpTimeLabel}
            id="pickUpTime"
            value={pickUpTime ?? ''}
            formattedDate={this.getFormattedDate(pickUpDate ?? '')}
            {...commonProps}
          />
        </UitkLayoutGridItem>

        <UitkLayoutGridItem>
          <CarPickUpDropOffTimeSelect
            label={this.dropOffTimeLabel}
            id="dropOffTime"
            invalid={this.state.invalidText}
            value={dropOffTime ?? ''}
            formattedDate={this.getFormattedDate(dropOffDate ?? '')}
            domRef={this.dropOffRef}
            {...commonProps}
          />
        </UitkLayoutGridItem>
      </UitkLayoutGrid>
    );

    const setExpandoLinkIsVisible = (isVisible: boolean) => {
      this.expandoIsVisible = isVisible;
    };

    return (
      <UitkLayoutGridItem
        colSpan={{
          small: 1,
          medium: 3,
        }}
      >
        <div>
          <Viewport>
            <ViewSmall>
              <>
                <UitkSpacing margin={{ block: 'three' }}>
                  <hr />
                </UitkSpacing>
                <UitkHeading tag="h4" size={6}>
                  <LocalizedText message="multiItem.searchWizard.mobile.carLabel" />
                </UitkHeading>
                {timeSelect}
              </>
            </ViewSmall>
            <ViewMedium>
              <UitkExpandoLink
                size="medium"
                expandoTitle={this.pickUpDropOffToggleText}
                isVisible={this.expandoLinkIsVisible}
                data-stid="expand-multiitem-search-wizard-car-fields"
                onToggle={() => {
                  setExpandoLinkIsVisible(!this.expandoLinkIsVisible);
                }}
              >
                {timeSelect}
              </UitkExpandoLink>
            </ViewMedium>
          </Viewport>
        </div>
      </UitkLayoutGridItem>
    );
  }
}
